/**
 * Navigate to a Patchbay route, and do something visible even when it is the page already on screen (issue #98):
 * re-run the route's loaders and scroll the page back to the top. A plain navigate to the current URL is a no-op,
 * which read as "clicking the case I'm on does nothing".
 */
import type { AnyRouter } from '@tanstack/react-router'

/** Scroll the window and every marked scroll pane (`data-scroll-top`) to the top. */
export function scrollPageToTop(): void {
  if (typeof window === 'undefined') return
  window.scrollTo?.({ top: 0 })
  for (const el of document.querySelectorAll<HTMLElement>('[data-scroll-top]')) {
    if (typeof el.scrollTo === 'function') el.scrollTo({ top: 0 })
    else el.scrollTop = 0
  }
}

function samePath(a: string, b: string): boolean {
  const strip = (s: string) => decodeURIComponent(s.split('#')[0]?.split('?')[0] ?? '').replace(/\/+$/, '')
  return strip(a) === strip(b)
}

export async function goToHref(router: Pick<AnyRouter, 'navigate' | 'invalidate' | 'state'>, href: string): Promise<void> {
  const here = router.state.location.pathname
  if (samePath(here, href)) {
    await router.invalidate()
  } else {
    await router.navigate({ href })
  }
  requestAnimationFrame(scrollPageToTop)
}

export function goToCase(router: Pick<AnyRouter, 'navigate' | 'invalidate' | 'state'>, caseNumber: string): Promise<void> {
  return goToHref(router, `/case/${encodeURIComponent(caseNumber)}`)
}
